import React from "react";
import PropTypes from "prop-types";
import cn from "classnames";

import convertHTMLtoJSX from "@/next-src/utils/convertHTMLtoJSX";
import ButtonLink from "@/next-src/components/ui/button-link/ButtonLink";

import ResponsiveImage from "@/next-src/components/ui/responsive-image/ResponsiveImage";
import useWindowDimensions from "@/next-src/hooks/useWindowDimensions";

import comCSS from "./MembershipCard.module.scss";
import Calendly from "@/next-src/components/ui/calendly/Calendly";

const MembershipCard = ({ className = "", data = {} }) => {
  const { width: windowWidth } = useWindowDimensions();
  const {
    titleMobile,
    textMobile,
    buttonMobile,
    icon,
    bgImage,
    recommended,
    hideIcon,
    calendlyButtonSuccessMobile,
    calendlyButtonSuccess,
  } = data || {};
  let { title, text, button } = data;
  if (windowWidth < 992) {
    title = titleMobile;
    text = textMobile;
    button = buttonMobile;
  }

  return (
    <div
      className={cn(className, comCSS.wrapper, "membership-card", {
        recommended: recommended?.isActive,
      })}
    >
      <div className="membership-card-inner-wrap">
        <div>
          <div className="membership-card-top-wrap">
            {!hideIcon ? (
              <ResponsiveImage
                src={icon.src}
                width={64}
                height={64}
                lazyLoading
                alt={icon.alt}
                className="program-icon"
              />
            ) : (
              <div className={cn("program-icon", comCSS.iconPlaceholder)} />
            )}
            {recommended?.isActive && (
              <div className="small-tag recommended">
                <div>{convertHTMLtoJSX(recommended?.text)}</div>
              </div>
            )}
          </div>
          <div className="margin-bottom margin-xsmall">
            {convertHTMLtoJSX(title)}
          </div>
          <div className="margin-bottom margin-large">
            <div className="max-width-xxxsmall">{convertHTMLtoJSX(text)}</div>
          </div>
        </div>

        {button.calendlyLink ? (
          <Calendly
            classNameBtn={cn("is-icon", comCSS.calendlyBtn, {
              "is-secondary is-alternate": !recommended?.isActive,
            })}
            classNameBtnSuccess={comCSS.calendlyBtn}
            isPulsingIcon
            data={{
              calendlyButton: button,
              calendlyButtonMobile: button,
              calendlyButtonSuccess,
              calendlyButtonSuccessMobile: calendlyButtonSuccessMobile,
            }}
          />
        ) : (
          <ButtonLink
            data={button}
            className={cn("is-icon", {
              "is-secondary is-alternate": !recommended?.isActive,
            })}
            isPulsingIcon
          />
        )}
      </div>
      <div className={cn("program-card-gradient-wrap", comCSS.bgImageWrapper)}>
        <ResponsiveImage
          src={bgImage.src}
          width={480}
          height={480}
          lazyLoading
          alt={bgImage.alt}
          sizes="(max-width: 479px) 100vw, 480px"
          className={cn("program-card-gradient-img", comCSS.bgImage)}
        />
      </div>
    </div>
  );
};

MembershipCard.propTypes = {
  className: PropTypes.string,
  data: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    titleMobile: PropTypes.string,
    text: PropTypes.string,
    textMobile: PropTypes.string,
    button: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    buttonMobile: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    calendlyButtonSuccess: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    calendlyButtonSuccessMobile: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
    }),
    icon: PropTypes.shape({
      id: PropTypes.string,
      src: PropTypes.string,
      alt: PropTypes.string,
    }),
    bgImage: PropTypes.shape({
      id: PropTypes.string,
      src: PropTypes.string,
      alt: PropTypes.string,
    }),
    recommended: PropTypes.shape({
      isActive: PropTypes.bool,
      text: PropTypes.string,
    }),
  }),
};

export default MembershipCard;
